import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer rounded-lg text-sm font-normal ring-offset-background transition-colors duration-200 focus-visible:outline-none focus-visible:ring-offset-0 focus-visible:border-gray-500 disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        active: 'border border-transparent bg-[#3D2EFF1A] text-[#3D2EFF]',
        default: 'bg-button-primary text-white hover:bg-button-primary/90',
        destructive: 'border border-transparent bg-red-100 text-red-600 hover:bg-red-200',
        ghost: 'text-[#1F1C1A] hover:bg-accent hover:text-accent-foreground',
        link: 'text-[#3D2EFF] font-medium hover:underline hover:text-blue-800 disabled:hover:no-underline',
        outline: 'border border-gray-200 bg-background hover:bg-accent hover:text-accent-foreground',
        primary: 'bg-button-primary text-white hover:bg-button-primary/90',
        secondary: 'bg-[#F5F5F5] text-[#1F1C1A] hover:bg-[#3D2EFF1A]',
        text: 'bg-transparent text-[#3D2EFF] hover:underline',
      },
      size: {
        default: 'h-10 px-6',
        icon: 'h-8 w-8 p-0',
        lg: 'h-12 px-8',
        sm: 'h-8 px-3',
        xs: 'h-7 px-2 py-0.5 text-xs gap-1',
      },
    },
    compoundVariants: [
      {
        class: 'h-fit px-0',
        variant: 'link',
      },
    ],
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
  },
);

export type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
  };

const LoadingSpinner = () => (
  <svg
    className="animate-spin h-5 w-5 text-current"
    fill="none"
    role="status"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      fill="currentColor"
    />
  </svg>
);

const Button = (props: ButtonProps) => {
  const { asChild = false, children, className, disabled, loading = false, size, variant, ...otherProps } = props;
  const Comp = asChild ? Slot : 'button';
  const { ref, ...restProps } = otherProps;
  const showSpinner = loading && !asChild;

  return (
    <Comp
      className={cn(buttonVariants({ className, size, variant }))}
      disabled={disabled || loading}
      ref={ref}
      {...restProps}
    >
      {showSpinner ? <LoadingSpinner /> : children}
    </Comp>
  );
};
Button.displayName = 'Button';

export { Button, buttonVariants };
